/**
 * CorrectGuessToast: full-screen celebratory flash on a correct guess.
 *
 * Fires when the local player's `game.lastVerdict.kind === "correct"`.
 * Renders a centered overlay (CheckMark mark + "Got it!" + the word
 * they guessed) for ~1500ms, then auto-dismisses. The inline
 * VerdictDisplay inside <GuessInput> still surfaces the verdict text
 * for accessibility + the audience-guess-sheet flow; this is the
 * extra emotional payoff.
 *
 * Mounted at room scope (RoomShell) so it shows over drawing, reveal,
 * or whatever phase the verdict happened to land on. pointerEvents:
 * none so the overlay never blocks input underneath.
 *
 * The `at` timestamp on the verdict gives every correct guess a fresh
 * identity, so a second correct in the same turn (shouldn't happen in
 * practice, but doesn't hurt to be safe) re-triggers the animation.
 */

import { useEffect, useState } from "preact/hooks";
import { CheckMark } from "../marks/v1/CheckMark.js";
import { SCRIBBLE_IN_CLASS } from "../anim/scribble-in.js";

export const CORRECT_GUESS_TOAST_DURATION_MS = 1500;

/** Exit-fade length: the tail of durationMs, not added to it. */
export const TOAST_EXIT_MS = 180;

type Verdict = { kind: "correct" | "close" | "miss"; text: string; at: number };

type CorrectGuessToastProps = {
  /** Latest server verdict for the local player. */
  verdict: Verdict | undefined;
  /** The word the local player just guessed (currentTurn.word once correct). */
  word: string | undefined;
  /** Override for tests / reduced-motion. */
  durationMs?: number;
};

export function CorrectGuessToast({
  verdict,
  word,
  durationMs = CORRECT_GUESS_TOAST_DURATION_MS,
}: CorrectGuessToastProps) {
  // The shown verdict is captured locally so the toast can outlive a
  // prop-level reset to undefined (e.g., a fresh GUESS_MISS landing
  // mid-fadeout shouldn't wipe the "Got it!" overlay prematurely).
  const [shownAt, setShownAt] = useState<number | null>(null);
  const [shownWord, setShownWord] = useState<string | null>(null);
  // Exit beat (audit 2026-06-11): the toast used to hard-unmount at
  // durationMs: it now fades for the last TOAST_EXIT_MS first.
  const [leaving, setLeaving] = useState(false);

  // Effect 1: watch the incoming verdict and arm the toast on each new
  // correct. Non-correct verdicts MUST NOT touch the timer, see Effect 2.
  useEffect(() => {
    if (!verdict || verdict.kind !== "correct") return;
    setShownAt(verdict.at);
    setShownWord(word ?? null);
    setLeaving(false);
  }, [verdict?.at, verdict?.kind, word]);

  // Effect 2: own the dismiss timer off `shownAt` so a subsequent miss /
  // close verdict can't strand the toast. Previously the timer lived
  // alongside Effect 1 and its cleanup fired on every verdict change,
  // a miss arriving mid-toast cleared the timer and the new effect
  // early-returned without re-arming, leaving "Got it!" on screen
  // forever.
  useEffect(() => {
    if (shownAt === null) return;
    const exitId = setTimeout(() => setLeaving(true), Math.max(0, durationMs - TOAST_EXIT_MS));
    const id = setTimeout(() => setShownAt(null), durationMs);
    return () => {
      clearTimeout(exitId);
      clearTimeout(id);
    };
  }, [shownAt, durationMs]);

  if (shownAt === null) return null;

  return (
    <div
      class="correct-guess-toast"
      data-testid="correct-guess-toast"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div
        class={`correct-guess-toast-card ${SCRIBBLE_IN_CLASS}${leaving ? " correct-guess-toast-card--leaving" : ""}`}
      >
        <div class="correct-guess-toast-icon">
          <CheckMark size={64} />
        </div>
        <div class="correct-guess-toast-headline">Got it!</div>
        {shownWord && (
          <div class="correct-guess-toast-word" data-testid="correct-guess-toast-word">
            "{shownWord}"
          </div>
        )}
      </div>
    </div>
  );
}
